import React, { useEffect, useState } from "react";
import { Button } from "../../design/button";
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogDescription,
  DialogTitle,
} from "../../design/dialog";
import { Input } from "../../design/input";
import { Field, Fieldset, FieldGroup, Label } from "../../design/fieldset";
import { Textarea } from "../../design/textarea";
import { useVisitationSchedule } from "../../contexts/VisitationScheduleContext";
import { ChooseVisitationScheduleTypeStep } from "./components/ChooseVisitationScheduleTypeStep";
import { PlusIcon } from "@heroicons/react/20/solid";
import { VisitationScheduleType } from "../../models/VisitationScheduleType";
import { VisitationSchedule } from "../../models/VisitationSchedule";
import { on } from "events";
import { VisitationScheduleForm } from "../../models/Forms/VisitationScheduleForm";
import { EditVisitationRotationStep } from "./components/EditVisitationRotationStep";
import { generateRotationBlocks } from "../../helpers/generateRotationBlocks";
import { useMatter } from "../../contexts/MatterContext";
import moment from "moment";

interface Props {
  type?: VisitationScheduleType;
  className?: string;
  children?: React.ReactNode;
  onSave?: (id: VisitationSchedule["_id"]) => void;
}

const CreateVisitationScheduleButton: React.FC<Props> = (props) => {
  const { createVisitationSchedule } = useVisitationSchedule();
  const [isOpen, setIsOpen] = useState(false);
  const [form, setForm] = useState<VisitationScheduleForm>({
    name: "",
    type: props.type,
    startDate: moment().format("YYYY-MM-DD"),
    endDate: undefined,
    interval: "1-week",
    rotation: undefined,
  } as VisitationScheduleForm);
  const [isUploading, setIsUploading] = useState(false);
  const [isRotationStepValid, setIsRotationStepValid] = useState(true);

  const handleSubmit = async () => {
    if (!form.name || !form.type) return;
    setIsUploading(true);
    try {
      const id = await createVisitationSchedule(form);
      clearForm();
      props.onSave?.(id);
    } catch (err) {
      console.error("Error creating visitationSchedule:", err);
      alert("Something went wrong. Please try again.");
    }
    setIsUploading(false);
  };

  const clearForm = () => {
    setIsOpen(false);
    setIsUploading(false);
  };

  return (
    <>
      <Button
        className={props.className}
        type="button"
        onClick={() => setIsOpen(true)}
      >
        {props.children ?? (
          <>
            <PlusIcon className="w-4 h-4" />
            <span className="hidden sm:block">Create Visitation Schedule</span>
          </>
        )}
      </Button>

      <Dialog open={isOpen} onClose={setIsOpen}>
        {!form.type && (
          <DialogTitle>
            What type of schedule do you want to create?
          </DialogTitle>
        )}
        {form.type && <DialogTitle>Create Visitation Schedule</DialogTitle>}
        {!form.type && (
          <DialogDescription>
            A rotation signifies the basic pattern of your parenting schedule.
            Choose from the list of common rotations or select Custom to build a
            parenting schedule rotation from scratch.
          </DialogDescription>
        )}
        <DialogBody>
          {!form.type && (
            <ChooseVisitationScheduleTypeStep form={form} setForm={setForm} />
          )}
          {form.type && (
            <EditVisitationRotationStep
              form={form}
              setForm={setForm}
              onValidationChange={(isValid) => setIsRotationStepValid(isValid)}
            />
          )}
        </DialogBody>

        <DialogActions>
          {!form.type && (
            <Button plain onClick={clearForm}>
              Cancel
            </Button>
          )}
          {form.type && (
            <Button
              plain
              onClick={() =>
                setForm({ ...form, type: undefined, rotation: undefined })
              }
            >
              Back
            </Button>
          )}
          {form.rotation && (
            <Button
              color="green"
              disabled={isUploading || !isRotationStepValid}
              onClick={handleSubmit}
            >
              {isUploading ? "Creating..." : "Create"}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CreateVisitationScheduleButton;
